import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FONT_SANS, getMapboxAccessToken, MAPBOX_HERO_VIEW, MAPBOX_STYLE } from '../constants';
import { useLanguage } from '../context/LanguageContext';
import { colors } from '../theme/colors';
import { AnchorSecondary, LinkPrimary } from './Button';
import DownloadSection from './DownloadSection';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: none; }
`;

const Hero = styled.section`
  position: relative;
  width: 100%;
  min-height: 100svh;
  overflow: hidden;
  background: ${colors.bg};
`;

const MapMount = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: auto;

  .mapboxgl-canvas {
    outline: none;
  }

  .mapboxgl-ctrl-logo,
  .mapboxgl-ctrl-attrib {
    display: none !important;
  }

  .mapboxgl-ctrl-bottom-right {
    z-index: 3;
    pointer-events: auto;
  }
`;

const MapFallback = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  background: linear-gradient(160deg, #e8e4df 0%, ${colors.bg} 100%);
`;

const BottomBlur = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: min(240px, 32vh);
  background: linear-gradient(to bottom, rgba(246, 244, 240, 0) 0%, ${colors.bg} 100%);
  pointer-events: none;
  z-index: 1;
`;

const TopFade = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 120px;
  background: linear-gradient(to bottom, rgba(246, 244, 240, 0.55) 0%, transparent 100%);
  pointer-events: none;
  z-index: 1;
`;

const Content = styled.div`
  position: relative;
  z-index: 2;
  min-height: 100svh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 7rem clamp(20px, 5vw, 48px) 4rem;
  text-align: center;
  animation: ${fadeIn} 0.7s ease both;
  pointer-events: none;

  a,
  button {
    pointer-events: auto;
  }
`;

const TextBlock = styled.div`
  pointer-events: none;
  max-width: 820px;
`;

const Headline = styled.h1`
  font-family: ${FONT_SANS};
  font-size: clamp(2rem, 5.5vw, 3.25rem);
  line-height: 1.12;
  letter-spacing: -0.03em;
  margin: 0 0 16px;
  max-width: 820px;
  color: ${colors.ink};
  font-weight: 800;
  text-shadow: 0 2px 24px rgba(246, 244, 240, 0.9);
`;

const Sub = styled.p`
  font-family: ${FONT_SANS};
  font-size: clamp(1rem, 2vw, 1.2rem);
  line-height: 1.6;
  max-width: 560px;
  min-height: 3em;
  margin: 0 0 32px;
  color: ${colors.inkSoft};
  text-shadow: 0 1px 16px rgba(246, 244, 240, 0.85);
`;

const CtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin-bottom: 28px;
`;

const HERO_DELAY_MS = 600;
const TYPE_MS = 28;

function MapHero() {
  const { t } = useLanguage();
  const [typed, setTyped] = useState('');
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const token = getMapboxAccessToken();

  useEffect(() => {
    const text = t.hero.typed;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setTyped(text);
      return undefined;
    }

    let cancelled = false;
    setTyped('');
    const startTimer = window.setTimeout(() => {
      let i = 0;
      const tick = () => {
        if (cancelled) return;
        setTyped(text.slice(0, i));
        i += 1;
        if (i <= text.length) {
          window.setTimeout(tick, TYPE_MS);
        }
      };
      tick();
    }, HERO_DELAY_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(startTimer);
    };
  }, [t.hero.typed]);

  useEffect(() => {
    if (!token || !mapContainerRef.current || mapRef.current) return undefined;

    mapboxgl.accessToken = token;
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: MAPBOX_STYLE,
      center: MAPBOX_HERO_VIEW.center,
      zoom: MAPBOX_HERO_VIEW.zoom,
      pitch: MAPBOX_HERO_VIEW.pitch,
      bearing: MAPBOX_HERO_VIEW.bearing,
      /** Trackpad / wheel scrolls the page; hold ⌘/Ctrl + scroll to zoom the map. */
      cooperativeGestures: true,
      attributionControl: false,
      logoPosition: 'bottom-left',
      antialias: true,
    });

    map.addControl(new mapboxgl.NavigationControl({ visualizePitch: true }), 'bottom-right');

    mapRef.current = map;

    map.on('style.load', () => {
      try {
        if (!map.getSource('mapbox-dem')) {
          map.addSource('mapbox-dem', {
            type: 'raster-dem',
            url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
            tileSize: 512,
            maxzoom: 14,
          });
        }
        if (!map.getTerrain()) {
          map.setTerrain({ source: 'mapbox-dem', exaggeration: 1 });
        }
      } catch {
        /* style may already define terrain */
      }
    });

    map.on('load', () => {
      map.resize();
    });

    map.on('error', (event) => {
      const message = event?.error?.message;
      if (message) console.warn('[MapHero]', message);
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [token]);

  return (
    <Hero aria-label={t.a11y.exploreMap}>
      {token ? <MapMount ref={mapContainerRef} aria-hidden="true" /> : <MapFallback aria-hidden="true" />}
      <TopFade aria-hidden="true" />
      <BottomBlur aria-hidden="true" />
      <Content>
        <TextBlock>
          <Headline>{t.hero.headline}</Headline>
          <Sub aria-live="polite">{typed}</Sub>
        </TextBlock>
        <CtaRow>
          <LinkPrimary to="/support">{t.hero.ctaPrimary}</LinkPrimary>
          <AnchorSecondary href="#events">{t.hero.ctaSecondary}</AnchorSecondary>
        </CtaRow>
        <DownloadSection compact />
      </Content>
    </Hero>
  );
}

export default MapHero;
